<template>
  <div ref="paymentElementRef"></div>
</template>
<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  useContext,
} from '@nuxtjs/composition-api';

import {
  usePaymentProvider,
  useCart,
  useStripeOrder
} from '~/composables';

import { useShippingCountryStore } from '~/modules/checkout/stores/shippingCountry';

import { PaymentTypeEnums } from '~/modules/checkout/stripe/types';

export default defineComponent({
  name: 'Stripe',
  props: {
    apiKey: {
      type: String,
      default: '',
      required: true
    },
    paymentMethods: {
      code: String,
      title: String,
      required: true
    }
  },
  emits: ['status', 'stripe-ready', 'make-order'],
  setup(props, {emit}) {
    const {app, $config} = useContext();
    const stripe = ref();
    const paymentElementRef = ref();
    const paymentElement = ref();
    const currency = app.$vsf.$magento.config.state.getCurrency().toLowerCase();
    const {cart} = useCart();
    const {save: setPaymentMethodOnCart} = usePaymentProvider();
    const shippingCountryStore = useShippingCountryStore();
    const elements = ref();
    const {placeStripeOrder} = useStripeOrder();
    const stripePaymentType = ref();

    const customHeaders = computed(() =>
        shippingCountryStore.shippingCountry.isShippingCountrySpain
          ? { store: 'es' }
          : {}
    );

    const initStripe = async () => {
      stripe.value = Stripe(props.apiKey);
    };

    const isTabby = (paymentMethods) => Boolean(paymentMethods.find(method => method.code === 'tabby_installments'));

    const createPaymentMethod = async () => {
      const paymentMethodResults = await stripe.value.createPaymentMethod({
        elements: elements.value,
        params: {
          billing_details: {
            'address': {
              'city': cart.value.billing_address.city,
              'country': cart.value.billing_address.country.code,
              'line1': cart.value.billing_address.street[0],
              'line2': cart.value.billing_address.street[1] || '',
              'postal_code': cart.value.billing_address.postcode,
              'state': cart.value.billing_address.region.code
            },
            'email': cart.value.email,
            'name': cart.value.billing_address.firstname + ' ' + cart.value.billing_address.lastname,
            'phone': cart.value.billing_address.telephone
          }
        }
      });

      if(paymentMethodResults.paymentMethod) {

        if(stripePaymentType.value === PaymentTypeEnums.tabby) {
          await setPaymentMethodOnCart(
            {paymentMethod: {code: 'external_tabby',}},
            customHeaders.value
          );
        } else {
          await setPaymentMethodOnCart(
            {paymentMethod: {
              code: 'stripe_payments',
              stripe_payments: {
                payment_method: paymentMethodResults.paymentMethod.id,
                save_payment_method: true
              }
            }},
            customHeaders.value
          );
        }
        return true;
      } else {
        return false;
      }
    };

    const handleError = (error) => {
      console.log(error);
    };

    const isSuccessful = (paymentIntent) => {
      switch (paymentIntent.status) {
        case 'succeeded':
          // The payment was captured automatically
          return true;

        case 'requires_capture':
          // The authorization succeeded and the payment needs to be captured manually
          return true;

        case 'processing':
          // Very rare case where the authorization is still being processed.
          return true;

        case 'requires_action':
          // Some required actions are offline, i.e. when placing an order using a voucher like OXXO or Konbini
          if(paymentIntent.next_action && paymentIntent.next_action.type && paymentIntent.next_action.type !== 'use_stripe_sdk') {
            // use_stripe_sdk means that handleNextAction should be used. If not, we treat this as an offline required action
            return true;
          }
          break;
      }
      return false;
    };

    const handlePaymentIntent = async (clientSecret) => {
      // Handle payment intent and its status
      try {
        const paymentIntent = await stripe.value.retrievePaymentIntent(clientSecret);
        if(paymentIntent.error) {
          return false;
        } else if(paymentIntent.paymentIntent) {

          let return_url;
          if($config.websiteCode.endsWith('/')) {
            return_url = `${window.location.origin}/${$config.websiteCode}/checkout/payment-processing?payment_code=stripe`;
          } else if( $config.websiteCode === '') {
            return_url = `${window.location.origin}${$config.websiteCode}/checkout/payment-processing?payment_code=stripe`;
          }
          else {
            return_url = `${window.location.origin}/${$config.websiteCode}/checkout/payment-processing?payment_code=stripe`;
          }

          await stripe.value.confirmPayment({
            clientSecret: clientSecret,
            confirmParams: {
              return_url
            }
          });

          // 3D Secure
          if(paymentIntent.paymentIntent.status === 'requires_action') {
            const result = await stripe.value.handleNextAction({clientSecret});
            if(result.paymentIntent) {
              return isSuccessful(result.paymentIntent);
            } else {
              return false;
            }
          } else {
            return isSuccessful(paymentIntent.paymentIntent);
          }
        } else {
          // API call crashed
          return false;
        }
      }
      catch (error) {
        console.error('An error occurred:', error);
        return false;
      }
    };

    const useStripe = async () => {

      // Process payment using Stripe
      const {error: submitError} = await elements.value.submit();
      if(submitError) {
        handleError(submitError);
        return;
      }
      await createPaymentMethod();
      const response = await placeStripeOrder(cart.value.id, customHeaders.value);

      const orderData = response?.placeOrder?.order;
      if (orderData?.order_number) emit('make-order', orderData.order_number || '');

      const parseResponse = async (response) => {
        if (orderData?.client_secret) {
          return (await handlePaymentIntent(orderData.client_secret, orderData.order_number)) ? orderData : false;
        }
        if (response?.errors?.length) return response.errors[0];
        return response?.message || false;
      };

      return await parseResponse(response);
    };

    const initPaymentElement = async () => {
      // Initialize payment element

      initStripe();

      const options = {
        mode: 'payment',
        amount: cart.value.prices.grand_total.value * 100,
        currency: currency,
        paymentMethodCreation: 'manual',
        fields: {
          billingDetails: {
            address: {
              country: 'never',
              postalCode: 'never'
            }
          }
        },
        appearance: {
          theme: 'flat',
          variables: {
            fontFamily: 'Calleo, sans-serif',
            fontSizeBase: '16px',
            borderRadius: '0px',
            colorPrimary: '#907C4B',
            colorBackground: 'white',
            colorText: '#0F0F0F',
            colorTextSecondary: '#686864',
            focusBoxShadow: 'none',
            spacingGridColumn: '1rem',
            spacingGridRow: '1rem',
            colorIconCardCvc: 'transparent',
          },
          rules: {
            '.Input': {
              padding: '8px 16px',
              border: '1px solid #0F1319',
              backgroundColor: 'var(--colorBackground)',
              fontSize: '16px',
              lineHeight: '22px',
              borderRadius: '4px'
            },
            '.Input--empty::placeholder': {
              color: 'var(--colorTextSecondary)',
            },
            '.Input:focus': {
              outline: '1px solid #0F1319',
            },
            '.Input:disabled, .Input--invalid:disabled': {
              color: 'lightgray'
            },
            '.Label': {
              opacity: '0',
              fontSize: '0',
              marginBottom: '0',
            }
          }
        },
        ...(isTabby(props.paymentMethods) && {externalPaymentMethodTypes: ['external_tabby']})
      };
      // Set up Stripe.js and Elements to use in checkout form
      elements.value = stripe.value.elements(options);

      // Create and mount the Payment Element
      paymentElement.value = elements.value.create('payment', options);
      paymentElement.value.mount(paymentElementRef.value);

      // Load payment methods for later access
      // paymentMethods.value = await load();
    };

    const validate = () => {

      // Check if all required fields for the selected payment method in the Payment Element have been filled with potentially valid input
      paymentElement.value.on('change', (event) => {
        if(event) {
          stripePaymentType.value = event.value.type;
          emit('status', event);
        }
      });

      paymentElement.value.on('ready', (event) => {
        if(event) {
          emit('stripe-ready', event);
        }
      });
    };

    onMounted(() => {
      initPaymentElement();
      validate();
      useStripe();
    });

    return {
      paymentElementRef,
      useStripe
    };
  }
});
</script>
